/* eslint-disable no-continue, no-await-in-loop */
const MAPTILER_TILE_CACHE = 'maptiler-tiles';
const KASUNGU_BBOX = {
  sw: { lng: 32, lat: -14.5 },
  ne: { lng: 35, lat: -11.5 },
};
const PREFETCH_ZOOM_RANGE = { min: 6, max: 10 };
const PREFETCH_CONCURRENCY = 32;
const PREFETCH_LAST_RUN_KEY = 'offline-maptiler-prefetch:kasungu:z6-10:v1';
const PREFETCH_REFRESH_MS = 13 * 24 * 60 * 60 * 1000;
const MAX_LATITUDE = 85.05112878;
const TILE_TEMPLATE = `https://api.maptiler.com/tiles/v3/{z}/{x}/{y}.pbf?key=${import.meta.env.VITE_MAPTILER_TOKEN}`;

function clampLat(lat) {
  return Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, lat));
}

function lngLatToTile(lng, lat, zoom) {
  const n = 2 ** zoom;
  const clampedLat = clampLat(lat);
  const latRad = (clampedLat * Math.PI) / 180;
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  return { x, y };
}

function getTileRangeForBBox(bbox, zoom) {
  const sw = lngLatToTile(bbox.sw.lng, bbox.sw.lat, zoom);
  const ne = lngLatToTile(bbox.ne.lng, bbox.ne.lat, zoom);
  const xMin = Math.min(sw.x, ne.x);
  const xMax = Math.max(sw.x, ne.x);
  const yMin = Math.min(sw.y, ne.y);
  const yMax = Math.max(sw.y, ne.y);
  return {
    xMin,
    xMax,
    yMin,
    yMax,
  };
}

function buildTileUrl(template, {
  x, y, z,
}) {
  return template
    .replace('{z}', z)
    .replace('{x}', x)
    .replace('{y}', y);
}

async function prefetchTileUrls(urls) {
  if (!('caches' in window)) return;

  const cache = await caches.open(MAPTILER_TILE_CACHE);
  const queue = [...urls];

  const workers = Array.from({ length: PREFETCH_CONCURRENCY }, async () => {
    while (queue.length > 0) {
      const url = queue.shift();
      if (!url) return;

      try {
        const cached = await cache.match(url);
        if (cached) continue;

        const response = await fetch(url, { mode: 'cors' });
        if (!response) continue;
        if (!response.ok && response.type !== 'opaque') continue;

        await cache.put(url, response.clone());
      } catch (error) {
        // Ignore individual tile fetch failures
      }
    }
  });

  await Promise.all(workers);
}

async function prefetchOfflineMapTiles() {
  if (!navigator.onLine) return;

  const lastRun = localStorage.getItem(PREFETCH_LAST_RUN_KEY);
  if (lastRun) {
    const lastRunMs = Date.parse(lastRun);
    if (!Number.isNaN(lastRunMs) && Date.now() - lastRunMs < PREFETCH_REFRESH_MS) {
      return;
    }
  }

  const urls = new Set();

  for (let z = PREFETCH_ZOOM_RANGE.min; z <= PREFETCH_ZOOM_RANGE.max; z += 1) {
    const {
      xMin, xMax, yMin, yMax,
    } = getTileRangeForBBox(KASUNGU_BBOX, z);

    for (let x = xMin; x <= xMax; x += 1) {
      for (let y = yMin; y <= yMax; y += 1) {
        urls.add(buildTileUrl(TILE_TEMPLATE, {
          x, y, z,
        }));
      }
    }
  }

  await prefetchTileUrls(Array.from(urls));
  localStorage.setItem(PREFETCH_LAST_RUN_KEY, new Date().toISOString());
}

export {
  prefetchOfflineMapTiles,
};
